import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'

import { useAlert } from 'components/Shared/Alert'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import filterGroupsAPI, {
  ascGroupsParam,
} from 'api/ApiActions/Groups/FilterByGroup'
import type { Group as GroupType } from 'containers/Participants/Redux/Reducer/InitialStateType'
import {
  resetFilters,
  toggleHasFilterValueStatus,
} from 'redux/slices/FilterBuilder'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { fetchParticipantGroups } from 'redux/thunks/ParticipantGroups'
import {
  setActiveGroup,
  setProjectsOfAGroup,
} from 'redux/slices/ParticipantGroups'
import ParticipantInGroup from 'components/Participants/Group/ProjectsInGroup'
import {
  actionButtonStyles,
  dialogContentStyles,
} from 'components/Participants/Group/DeleteGroupModal/Styles'

type DeleteModalProps = {
  isOpen: boolean
  setModalVisibility: (isOpen: boolean) => void
  deleteGroup: GroupType
}

const DeleteGroupDialog = ({
  isOpen,
  setModalVisibility,
  deleteGroup,
}: DeleteModalProps) => {
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const theme: AdminUITheme = useTheme()
  const dispatch = useAppDispatch()
  const activeGroup = useAppSelector(
    (state) => state.participantGroups.activeGroup,
  )

  const [hasLoaded, setLoading] = useState(false)

  useEffect(() => {
    const getProject = async () => {
      await fetchProjectsAssociatedWithTheGroup(deleteGroup)
      setLoading(true)
    }

    getProject()
  }, [])

  const handleClose = () => {
    setModalVisibility(false)
  }

  const handleError = () => {
    showAlert({ type: 'error', title: t('alert.error.400') })
  }

  const handleSuccess = () => {
    showAlert({
      type: 'success',
      title: t('filters.participantGroup.delete.successMessage', {
        groupName: deleteGroup.name,
      }),
      isHTML: true,
    })
  }

  const fetchProjectsAssociatedWithTheGroup = async (group: GroupType) => {
    const payload = {
      groupId: group.id,
    }
    const response = await filterGroupsAPI.getProjectsInAGroup(payload)
    dispatch(setProjectsOfAGroup(response.data.data))
  }

  const handleClick = async () => {
    const result = await filterGroupsAPI.deleteGroup({
      segmentId: deleteGroup.id,
    })

    // If selected group is deleted then clear it
    if (deleteGroup.id === activeGroup?.id) {
      dispatch(setActiveGroup(null))

      // Call segment API and fetch the participants
      dispatch(toggleHasFilterValueStatus(true))
      dispatch(resetFilters())
    }

    const { code, status } = result

    if (status === 204) {
      handleSuccess()
      handleClose()

      dispatch(fetchParticipantGroups(ascGroupsParam))
    } else if (code === 'ERR_BAD_REQUEST') {
      handleError()
      handleClose()
    }
  }

  const content = ['groupDeleteInfo', 'groupDeleteConfirmation']

  const CustomDialogContent = (
    <>
      <Stack spacing={3}>
        <Typography variant="h5" component="h5">
          {t('filters.participantGroup.delete.deleteGroup')}
        </Typography>

        <Typography
          variant="body1"
          component="h5"
          sx={{ wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{
            __html: t('filters.participantGroup.delete.groupWarning', {
              groupName: deleteGroup.name,
            }),
          }}
        />

        {content.map((typography, index) => (
          <Typography
            variant="body1"
            component="h5"
            key={index}
            dangerouslySetInnerHTML={{
              __html: t(`filters.participantGroup.delete.${typography}`),
            }}
          />
        ))}
      </Stack>
      <Box sx={{ mt: '1.5rem !important' }}>
        <ParticipantInGroup />
      </Box>
    </>
  )

  const CustomActionButtons = (
    <>
      <Button
        sx={theme.ParticipantGrid.deleteGroupDialogCancelBtnMob}
        variant="outlined"
        onClick={handleClose}
      >
        {t('common.no')}
      </Button>
      <Button
        sx={theme.ParticipantGrid.deleteGroupDialogCancelBtnDesk}
        variant="text"
        onClick={handleClose}
      >
        {t('common.no')}
      </Button>
      <Button
        variant="contained"
        onClick={handleClick}
        data-testid="delete-group-dialog-yes"
      >
        {t('common.yes')}
      </Button>
    </>
  )

  const loadDialogContent = () => {
    if (hasLoaded) return CustomDialogContent

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="30.5rem"
      >
        <CircularProgress size={42} />
      </Box>
    )
  }

  return (
    // zIndex is needed to avoid delete tooltip showing on top of modal
    <Dialog open={isOpen} onClose={handleClose} sx={{ zIndex: 9999 }}>
      <DialogContent sx={dialogContentStyles}>
        {loadDialogContent()}
      </DialogContent>
      <DialogActions sx={actionButtonStyles}>
        {hasLoaded && CustomActionButtons}
      </DialogActions>
    </Dialog>
  )
}

export default DeleteGroupDialog
